// AnimatedImageCache: shared decoder and frame cache for animated GIF/WebP via WebCodecs
// - Caches decoded ImageBitmaps per URL (and options key)
// - Reference counts active users; cleans up when no users remain
// - Exposes acquire(url, options) -> { frames, totalDurationMs, width, height, mime, release }
//
// Notes:
// - Requires browser with WebCodecs ImageDecoder
// - Frames are ImageBitmaps; consumers must not mutate or close them
// - When release() drives refCount to 0, all ImageBitmaps are closed and entry is deleted

import { getMimeTypeFromExtension } from "@/components/graphics/Graphics";

const _cache = new Map(); // key -> { promise, data?, refCount }

function makeKey(url, opts) {
  const o = opts || {};
  return [url, o.colorSpaceConversion || 'default', o.premultiplyAlpha || 'none', o.imageOrientation || 'from-image'].join('|');
}

async function decodeToBitmaps(url) {
  // Fetch the resource
  const res = await fetch(url, { cache: 'force-cache' });
  const blob = await res.blob();
  const mime = blob.type || getMimeTypeFromExtension(url) || '';
  if (!mime.startsWith('image/')) {
    const err = new Error('Not an image: ' + mime);
    err.code = 'NOT_IMAGE';
    throw err;
  }

  const supportsImageDecoder = typeof window !== 'undefined' && typeof window.ImageDecoder === 'function';
  if (!supportsImageDecoder) {
    const err = new Error('ImageDecoder not supported');
    err.code = 'NO_DECODER';
    throw err;
  }

  // Get acceptable data type for ImageDecoder
  let data;
  if (typeof blob.stream === 'function') {
    try {
      data = blob.stream();
    } catch (_e) {
      data = await blob.arrayBuffer();
    }
  } else {
    data = await blob.arrayBuffer();
  }

  const decoder = new window.ImageDecoder({ data, type: mime });
  try {
    await decoder.tracks?.ready?.();
  } catch (_e) {
    // ignore
  }

  const frames = [];
  let totalDurationMs = 0;
  let width = 0, height = 0;

  const reportedCount = decoder.tracks?.selectedTrack?.frameCount;
  const hasReliableCount = Number.isFinite(reportedCount) && reportedCount > 1;

  if (hasReliableCount) {
    const frameCount = reportedCount;
    for (let i = 0; i < frameCount; i++) {
      const { image } = await decoder.decode({ frameIndex: i });
      const durationUs = image.duration ?? 0;
      const durationMs = Math.max(10, Math.round((durationUs || 0) / 1000));
      const bitmap = await createImageBitmap(image);
      if (i === 0) {
        width = bitmap.width;
        height = bitmap.height;
      }
      image.close();
      frames.push({ bitmap, durationMs });
      totalDurationMs += durationMs;
    }
  } else {
    // Fallback: probe frames sequentially until decode throws (more reliable across browsers)
    let more = true;
    for (let i = 0; more; i++) {
      try {
        const { image } = await decoder.decode({ frameIndex: i });
        const durationUs = image.duration ?? 0;
        const durationMs = Math.max(10, Math.round((durationUs || 0) / 1000));
        const bitmap = await createImageBitmap(image);
        if (i === 0) {
          width = bitmap.width;
          height = bitmap.height;
        }
        image.close();
        frames.push({ bitmap, durationMs });
        totalDurationMs += durationMs;
      } catch (e) {
        // Assume we've reached beyond the last frame
        more = false;
      }
    }

    if (frames.length <= 1) {
      // Cleanup any created bitmap and signal non-animated
      try { frames[0]?.bitmap?.close && frames[0].bitmap.close(); } catch (_e) { /* ignore close errors */ }
      decoder.close?.();
      const err = new Error('Not animated or single frame');
      err.code = 'NOT_ANIMATED';
      throw err;
    }
  }

  decoder.close?.();

  return { frames, totalDurationMs, width, height, mime };
}

export const AnimatedImageCache = {
  async acquire(url, options = {}) {
    if (!url) throw new Error('AnimatedImageCache.acquire: url is required');

    // CORS heuristic (keep consistent with component expectations)
    let isCrossOrigin = false;
    try {
      const u = new URL(url, typeof window !== 'undefined' ? window.location.href : undefined);
      const origin = (typeof window !== 'undefined') ? window.location.origin : '';
      const scheme = u.protocol.replace(':', '');
      isCrossOrigin = (u.origin !== origin) && scheme !== 'data' && scheme !== 'blob';
    } catch (_e) {
      isCrossOrigin = false;
    }
    if (isCrossOrigin && options.decodeCrossOrigin === false) {
      const err = new Error('Cross-origin decoding disabled');
      err.code = 'CROSS_ORIGIN_BLOCKED';
      throw err;
    }

    const key = makeKey(url, options.imageBitmapOptions);
    let entry = _cache.get(key);
    if (!entry) {
      entry = { refCount: 0, promise: null, data: null };
      entry.promise = decodeToBitmaps(url, options.imageBitmapOptions)
        .then(data => {
          entry.data = data;
          return data;
        })
        .catch(err => {
          // Ensure cache entry is removed on failure so future attempts can retry
          _cache.delete(key);
          throw err;
        });
      _cache.set(key, entry);
    }
    entry.refCount += 1;

    const release = () => {
      const current = _cache.get(key);
      if (!current) return; // already cleared
      current.refCount -= 1;
      if (current.refCount <= 0) {
        // Cleanup bitmaps and remove
        try {
          const data = current.data;
          if (data && Array.isArray(data.frames)) {
            data.frames.forEach(f => { try { f.bitmap.close && f.bitmap.close(); } catch (_e) { /* ignore close errors */ } });
          }
        } finally {
          _cache.delete(key);
        }
      }
    };

    // Return a structure that resolves when decoding is ready
    const data = await entry.promise;
    return { ...data, release };
  },

  // Optional: force clear all cached entries (e.g., on route change)
  clearAll() {
    for (const [key, entry] of _cache.entries()) {
      try {
        const data = entry.data;
        if (data && Array.isArray(data.frames)) {
          data.frames.forEach(f => { try { f.bitmap.close && f.bitmap.close(); } catch (_e) { /* ignore close errors */ } });
        }
      } finally {
        _cache.delete(key);
      }
    }
  }
};
